import { useEffect, useState } from "react";
import { isEqual, isBefore, isAfter } from "date-fns";

import useData from "./useData";
import usePage from "./usePage";

const usePayroll = () => {
  const { executeAction, getFromServer, updateRecord } = useData();
  const { showMessage } = usePage();

  const [payPeriods, setPayPeriods] = useState(null);
  const [fiscalYears, setFiscalYears] = useState(null);

  useEffect(() => {
    getPayPeriods();
  }, []);

  const getPayPeriod = (payPeriodId) => {
    if (!payPeriods) return null;

    const pp = payPeriods.find(
      (pp) => pp._id === payPeriodId || pp.id === payPeriodId,
    );

    if (pp) return pp;

    return null;
  };

  const buildPayPeriodUpdatePayload = (data) => {
    if (!data) return null;

    return {
      id: data.id || data._id || null,
      fiscalYear: data.fiscalYear,
      name: data.name,
      startDate: data.startDate,
      endDate: data.endDate,
      processDate: data.processDate,
      status: data.status,
      mileageRate: data.mileageRate,
      firstCallDeductionKm: data.firstCallDeductionKm,
      lastCallDeductionKm: data.lastCallDeductionKm,
    };
  };

  const updatePayPeriod = async (data) => {
    const payload = buildPayPeriodUpdatePayload(data);
    if (!payload) {
      showMessage("Pay period data is required", "error");
      return;
    }

    let valid = isValid(payload);
    if (!valid.isValid) {
      showMessage(valid.message, "error");
      return;
    }
    const results = await updateRecord("PayPeriod", payload);

    return results;
  };

  const isValid = (data) => {
    let valid = {
      isValid: true,
      message: "ok",
    };

    if (payPeriods) {
      for (const period of payPeriods) {
        if (period.id === data.id) continue;

        if (
          isEqual(data.startDate, period.startDate) ||
          isEqual(data.endDate, period.endDate)
        ) {
          valid.isValid = false;
          valid.message = "Dates conflict with another pay period";
          break;
        }

        if (
          isAfter(data.startDate, period.startDate) &&
          isBefore(data.startDate, period.endDate)
        ) {
          valid.isValid = false;
          valid.message = "Dates conflict with another pay period";
          break;
        }

        if (
          isAfter(data.endDate, period.startDate) &&
          isBefore(data.endDate, period.endDate)
        ) {
          valid.isValid = false;
          valid.message = "Dates conflict with another pay period";
          break;
        }
      }
    }

    if (valid.isValid) {
      if (isBefore(data.processDate, data.endDate)) {
        valid.isValid = false;
        valid.message = "Process date must be after end date";
      }
    }

    return valid;
  };

  const getPayPeriods = async () => {
    const results = await getFromServer({
      entityName: "PayPeriod",
    });

    if (results && results.length > 0) {
      setPayPeriods(results);
      setFiscalYears(() => {
        let years = Object.groupBy(results, (period) => period.fiscalYear);
        years = Object.keys(years).sort();

        return years;
      });
    } else {
      setPayPeriods([]);
      setFiscalYears([]);
    }
  };

  const refreshPayPeriods = async () => {
    setPayPeriods(null);
    setFiscalYears(null);
    await getPayPeriods();
  };

  const refreshPayPeriod = async (payPeriodId) => {
    try {
      const results = await executeAction({
        entityName: "PayPeriod",
        action: "getById",
        data: { id: payPeriodId },
      });

      if (results?.isSuccessful && results.data) {
        // Update payPeriods array with refreshed data
        setPayPeriods((prev) =>
          prev.map((pp) =>
            pp._id === payPeriodId || pp.id === payPeriodId ? results.data : pp,
          ),
        );
        return results.data;
      }
    } catch (err) {
      console.error("[usePayroll.refreshPayPeriod] Error:", err);
    }
    return null;
  };

  const togglePayrollLock = async (payPeriodId, locked) => {
    try {
      const results = await executeAction({
        entityName: "PayPeriod",
        action: "togglePayrollLock",
        data: { id: payPeriodId, payrollLocked: locked },
      });

      if (results?.isSuccessful) {
        // Update payPeriods array with new data
        setPayPeriods((prev) =>
          prev.map((pp) =>
            pp._id === payPeriodId || pp.id === payPeriodId
              ? { ...pp, payrollLocked: locked }
              : pp,
          ),
        );
        return results.data;
      } else {
        showMessage(
          results?.message || "Failed to update payroll lock status",
          "error",
        );
      }
    } catch (err) {
      console.error("[usePayroll.togglePayrollLock] Error:", err);
      showMessage("Error updating payroll lock status", "error");
    }
    return null;
  };

  return {
    getPayPeriods,
    payPeriods,
    fiscalYears,
    refreshPayPeriods,
    refreshPayPeriod,
    getPayPeriod,
    updatePayPeriod,
    togglePayrollLock,
  };
};

export default usePayroll;
